import { GridCellProps } from '@progress/kendo-react-grid';
import { Input, InputProps } from '@progress/kendo-react-inputs';
import { trim } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import GridCellBase from 'src/components/GridCellBase';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useRenameFileApi from 'src/services/useRenameFileApi';
const RenameFile = (props: GridCellProps) => {
  const { latestClickedId, setLatestClickedId, refreshPage } = useInvoiceScanManager();

  const { dataItem, field = '' } = props;
  const { attachId, description, isDeleted } = dataItem;
  const value = dataItem[field];

  const { onChange, dataIndex } = props;
  const openEdit = useCallback(() => setLatestClickedId(attachId), [attachId, setLatestClickedId]);
  const closeEdit = useCallback(() => setLatestClickedId(0), [setLatestClickedId]);
  const isEditable = attachId === latestClickedId;
  const { renameFile } = useRenameFileApi(true);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const extension = useMemo(() => description?.split('.').at(-1), [description]);

  const handleBlur = useCallback(async () => {
    if (value !== dataItem.NonEditableFilename) {
      const trimValue = trim(value);
      if (trimValue && !(trimValue === `.${extension}`)) {
        if (trimValue?.endsWith(`.${extension}`)) {
          await renameFile([
            {
              attachId: attachId,
              name: trimValue,
            },
          ]);
          toast.success('Renamed file successfully');
        } else {
          toast.error('Please provide the same file extension');
        }
      } else {
        toast.error('Please provide valid file name');
      }

      closeEdit();
      refreshPage();
    } else {
      closeEdit();
    }
  }, [
    value,
    dataItem.NonEditableFilename,
    extension,
    closeEdit,
    refreshPage,
    renameFile,
    attachId,
  ]);

  const handleChange = useCallback(
    (e: InputProps) => {
      onChange?.({
        dataIndex: dataIndex,
        dataItem: dataItem,
        field: field,
        syntheticEvent: e as SyntheticEvent,
        value: e.value,
      });
    },
    [dataIndex, dataItem, field, onChange]
  );

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <GridCellBase onClick={!isEditable ? openEdit : undefined} title={value} {...props}>
      {isEditable ? (
        <Input value={value} onBlur={handleBlur} onChange={handleChange} ref={inputRef} />
      ) : (
        <div className="flex items-center justify-between	">
          <span className="text-ellipsis overflow-hidden">{value}</span>
          {isDeleted && <span className="bg-red-100 ml-2 text-red-700 px-1 py-0.5">Deleted</span>}
        </div>
      )}
    </GridCellBase>
  );
};

export default RenameFile;
